import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { Estudiante } from '../../lib/supabase';

export default function InscripcionPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<Estudiante>({
    nombre_completo: '',
    email: '',
    telefono: '',
    pais: '',
    plan_pago: 'pago_unico',
    monto_total: 297,
    notas: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Limpiar error cuando el usuario empiece a escribir
    if (error) setError('');
    
    if (name === 'plan_pago') {
      const monto = value === 'pago_unico' ? 297 : 388;
      setFormData(prev => ({
        ...prev,
        [name]: value,
        monto_total: monto
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Verificar si el email ya está registrado
      const { data: existingStudent, error: checkError } = await supabase
        .from('estudiantes')
        .select('email')
        .eq('email', formData.email.toLowerCase().trim())
        .single();

      if (existingStudent) {
        setError('Este email ya está registrado. Si ya te inscribiste anteriormente, por favor contacta con nosotros por WhatsApp.');
        setLoading(false);
        return;
      }

      // Si no existe, continuar con la inscripción
      const { data, error: insertError } = await supabase
        .from('estudiantes')
        .insert([{
          ...formData,
          email: formData.email.toLowerCase().trim()
        }])
        .select()
        .single();

      if (insertError) {
        // Manejar diferentes tipos de errores
        if (insertError.code === '23505') {
          setError('Este email ya está registrado. Por favor usa otro email o contacta con nosotros.');
        } else {
          throw insertError;
        }
        setLoading(false);
        return;
      }

      // 2. Crear sesión de pago en Stripe
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          estudianteId: data.id,
          planPago: formData.plan_pago,
          email: formData.email,
          nombreCompleto: formData.nombre_completo,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear la sesión de pago');
      }

      const { url } = await response.json();

      // 3. Enviar notificación por WhatsApp (en segundo plano)
      const planTexto = formData.plan_pago === 'pago_unico' 
        ? 'Pago Único de $297' 
        : '4 Pagos de $97';
      
      const mensaje = `Hola, soy ${formData.nombre_completo}. Me acabo de inscribir en la Mentoría 10X con IA - ${planTexto}. Mi email es ${formData.email}`;
      
      const whatsappUrl = `https://wa.me/15626745958?text=${encodeURIComponent(mensaje)}`;
      window.open(whatsappUrl, '_blank');

      // 4. Redirigir a Stripe para completar el pago
      window.location.href = url;
      
    } catch (err: any) {
      console.error('Error al inscribir:', err);
      setError(err.message || 'Hubo un error al procesar tu inscripción. Por favor intenta de nuevo.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950 text-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/a66a38f581526f9f97f13d7c8b5f2655.png" 
              alt="Mentoria 10X con iA" 
              className="h-8 sm:h-10 w-auto"
            />
          </Link>
          <Link 
            to="/" 
            className="text-xs sm:text-sm text-gray-300 hover:text-orange-400 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-1 sm:mr-2"></i>
            <span className="hidden sm:inline">Volver al inicio</span>
            <span className="sm:hidden">Volver</span>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 sm:px-6 py-2 sm:py-3 backdrop-blur-sm mb-4 sm:mb-6">
              <i className="ri-user-add-line text-orange-400"></i>
              <p className="text-orange-400 text-xs sm:text-sm font-semibold tracking-wider">FORMULARIO DE INSCRIPCIÓN</p>
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-black mb-3 sm:mb-4">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Únete a la
              </span>
              <br />
              <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                Mentoría 10X
              </span>
            </h1>
            <p className="text-base sm:text-xl text-gray-400 px-4">
              Completa tus datos para asegurar tu plaza
            </p>
          </div>

          {/* Form */}
          <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/20 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 backdrop-blur-xl">
            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              {/* Nombre Completo */}
              <div>
                <label htmlFor="nombre_completo" className="block text-sm font-semibold text-gray-300 mb-2">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  id="nombre_completo"
                  name="nombre_completo"
                  required
                  value={formData.nombre_completo}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50 transition-colors text-sm"
                  placeholder="Tu nombre completo"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50 transition-colors text-sm"
                  placeholder="tu@email.com"
                />
              </div>

              {/* Teléfono */}
              <div>
                <label htmlFor="telefono" className="block text-sm font-semibold text-gray-300 mb-2">
                  Teléfono (WhatsApp) *
                </label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  required
                  value={formData.telefono}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50 transition-colors text-sm"
                  placeholder="+1 234 567 8900"
                />
              </div>

              {/* País */}
              <div>
                <label htmlFor="pais" className="block text-sm font-semibold text-gray-300 mb-2">
                  País *
                </label>
                <input
                  type="text"
                  id="pais"
                  name="pais"
                  required
                  value={formData.pais}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50 transition-colors text-sm"
                  placeholder="Tu país"
                />
              </div>

              {/* Plan de Pago */}
              <div>
                <label htmlFor="plan_pago" className="block text-sm font-semibold text-gray-300 mb-3">
                  Selecciona tu Plan de Pago *
                </label>
                <div className="space-y-3">
                  <label className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.plan_pago === 'pago_unico' 
                      ? 'border-orange-500 bg-orange-500/10' 
                      : 'border-white/10 bg-white/5 hover:border-white/20'
                  }`}>
                    <div className="flex items-center gap-3 mb-2 sm:mb-0">
                      <input
                        type="radio"
                        name="plan_pago"
                        value="pago_unico"
                        checked={formData.plan_pago === 'pago_unico'}
                        onChange={handleChange}
                        className="w-5 h-5 text-orange-500 cursor-pointer flex-shrink-0"
                      />
                      <div>
                        <div className="font-bold text-white text-sm sm:text-base">Pago Único - $297</div>
                        <div className="text-xs sm:text-sm text-gray-400">Mejor precio • Ahorras $91</div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-black px-3 py-1 rounded-full text-xs font-bold ml-8 sm:ml-0 w-fit">
                      RECOMENDADO
                    </div>
                  </label>

                  <label className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.plan_pago === '4_pagos' 
                      ? 'border-gray-500 bg-gray-500/10' 
                      : 'border-white/10 bg-white/5 hover:border-white/20'
                  }`}>
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="plan_pago"
                        value="4_pagos"
                        checked={formData.plan_pago === '4_pagos'}
                        onChange={handleChange}
                        className="w-5 h-5 text-gray-500 cursor-pointer flex-shrink-0"
                      />
                      <div>
                        <div className="font-bold text-white text-sm sm:text-base">4 Pagos de $97</div>
                        <div className="text-xs sm:text-sm text-gray-400">Por semana • Total: $388</div>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Notas Adicionales */}
              <div>
                <label htmlFor="notas" className="block text-sm font-semibold text-gray-300 mb-2">
                  Comentarios o Preguntas (Opcional)
                </label>
                <textarea
                  id="notas"
                  name="notas"
                  rows={4}
                  value={formData.notas}
                  onChange={handleChange}
                  maxLength={500}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50 transition-colors text-sm resize-none"
                  placeholder="¿Tienes alguna pregunta o comentario?"
                />
                <p className="text-xs text-gray-500 mt-1">Máximo 500 caracteres</p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-error-warning-line text-red-400 text-xl flex-shrink-0"></i>
                    <div>
                      <p className="text-red-400 text-sm font-semibold mb-1">Error al procesar tu inscripción</p>
                      <p className="text-red-300 text-sm">{error}</p>
                      {error.includes('email ya está registrado') && (
                        <a 
                          href="https://wa.me/15626745958?text=Hola,%20necesito%20ayuda%20con%20mi%20inscripción"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 mt-3 text-sm text-green-400 hover:text-green-300 transition-colors cursor-pointer"
                        >
                          <i className="ri-whatsapp-line"></i>
                          Contactar por WhatsApp
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-black px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:shadow-2xl hover:shadow-orange-500/50 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Procesando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-check-line text-lg sm:text-xl"></i>
                    Confirmar Inscripción
                  </span>
                )}
              </button>

              {/* Info */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-orange-400 text-xl flex-shrink-0 mt-0.5"></i>
                  <div className="text-sm text-gray-400">
                    <p className="mb-2">Al confirmar tu inscripción:</p>
                    <ul className="space-y-1 list-disc list-inside text-xs sm:text-sm">
                      <li>Recibirás un mensaje de WhatsApp para coordinar el pago</li>
                      <li>Te enviaremos los detalles de acceso a las sesiones</li>
                      <li>Serás agregado al grupo exclusivo de estudiantes</li>
                    </ul>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Security Badge */}
          <div className="flex justify-center mt-6 sm:mt-8">
            <div className="bg-white/5 border border-white/10 rounded-xl px-4 sm:px-6 py-2 sm:py-3 backdrop-blur-sm">
              <div className="flex items-center gap-2 text-gray-400 text-xs sm:text-sm">
                <i className="ri-shield-check-line text-green-400"></i>
                <span>Tus datos están protegidos y seguros</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
